/**
 * Segment-aware edge-label placement for Chart Arrange.
 *
 * Uniform-t sampling (the old pickLabelPosition) lands mid-corner or on a
 * short stub. This module scores label *rects* against cards, other labels,
 * and foreign edge segments, then reports how much an edge still needs to
 * grow so a clean 1-D slot exists. Labels only slide along the polyline
 * (`labelPosition`); they are never offset perpendicular to the edge.
 */

import {
    ENTITY_CARD_SHAPE_TYPE,
    ENTITY_CARD_WIDTH,
} from '../tldraw/customShapes/entityCardConstants';
import {
    anchorTFromNormalized,
    buildArcPolyline,
    buildElbowPolyline,
    facingSide,
    labelBoxAt,
    pointAlongPolyline,
    rectsOverlap,
    segmentIntersectsRect,
    sideFromNormalizedAnchor,
} from './chartArrowRouting';
import {
    estimateLabelBox,
    estimateLabelLineCount,
    LABEL_EDGE_PADDING,
} from './edgeLabelMetrics';

/** Inflate foreign cards by this much when testing label-box overlap. */
export const LABEL_CARD_PAD = 16;
/**
 * Extra inflate for the two endpoint cards (covers the review-icon cluster
 * at the card corner so a stub label is not crowded against it).
 */
export const LABEL_ENDPOINT_CARD_PAD = 32;
/** Keep the label box this far inside the hosting segment so it misses elbows. */
export const HOST_SEGMENT_INSET = 10;
/** Foreign stroke through the box, or this close to it, is a hard conflict. */
export const LABEL_FOREIGN_EDGE_PAD = 10;
/** Review-icon chip sits on the label pill's top-left corner. */
export const LABEL_REVIEW_ICON_PAD = 20;
/** Multi-line boxes must stay this far from a polyline vertex / elbow. */
export const LABEL_ELBOW_VERTEX_PAD = 12;
/** Near-miss closer than this is treated as an intersection. */
const FOREIGN_FATAL_NEAR_PX = 2;
/** Extra gap between two placed label boxes. */
const LABEL_BOX_PAD = 6;
const CARD_OVERLAP_PENALTY = 20000;
const LABEL_OVERLAP_PENALTY = 5000;
const VERTICAL_HOST_PENALTY = 60000;
const OWN_SEGMENT_CROSS_PENALTY = 50000;
const EDGE_THROUGH_LABEL_PENALTY = 8000;
const NEAR_MISS_PENALTY = 2800;
const NEAR_MISS_PER_PX = 400;
const VERTEX_PENALTY = 40000;
const SHORT_SPAN_PENALTY = 15000;
const LABEL_POINT_SEP = 100;
const HORIZONTAL_BONUS = 180;
const STRICT_SLOT_BONUS = 80;
const SPAN_SCORE_PER_PX = 0.35;
const MAX_REPAIR_ROUNDS = 4;
const MIN_LENGTHEN_WHEN_BLOCKED = 64;
export const NO_CLEAR_SLOT_ROUTE_PENALTY = 8000;
export const FOREIGN_HIT_ROUTE_PENALTY = 6000;
export const HORIZONTAL_SLOT_ROUTE_BONUS = 240;

/**
 * Along-segment footprint vs the inflated collision box (review icon).
 * Collision height always covers the wrapped tldraw box — never a
 * single-line-width stand-in that makes a 3-line pill look short.
 *
 * @param {string|null|undefined} label
 * @param {{w?: number, h?: number}|null} [labelSize]
 * @returns {{alongSegmentSize: {w: number, h: number}, collisionBoxSize: {w: number, h: number}}}
 */
export function labelBoxesForPlacement(label, labelSize) {
    const along = (labelSize && (labelSize.w > 0 || labelSize.h > 0))
        ? {w: labelSize.w || 0, h: labelSize.h || 0}
        : estimateLabelBox(label);
    return {
        alongSegmentSize: along,
        collisionBoxSize: {
            w: (along.w || 0) + LABEL_REVIEW_ICON_PAD,
            h: (along.h || 0) + LABEL_REVIEW_ICON_PAD,
        },
    };
}

/**
 * Along-segment size used for "does this run hold the label?".
 *
 * @param {string|null|undefined} label
 * @param {{w?: number, h?: number}|null} [labelSize]
 * @returns {{w: number, h: number}}
 */
export function placementLabelSize(label, labelSize) {
    return labelBoxesForPlacement(label, labelSize).alongSegmentSize;
}

/**
 * Collision rect: wrapped text box plus the review-icon pad on the top-left.
 *
 * @param {{x: number, y: number}} point
 * @param {{w: number, h: number}} alongSize
 * @returns {{x: number, y: number, w: number, h: number}}
 */
export function collisionLabelBoxAt(point, alongSize) {
    const box = labelBoxAt(point, alongSize);
    return {
        x: box.x - LABEL_REVIEW_ICON_PAD,
        y: box.y - LABEL_REVIEW_ICON_PAD,
        w: box.w + LABEL_REVIEW_ICON_PAD,
        h: box.h + LABEL_REVIEW_ICON_PAD,
    };
}

/**
 * @param {{x: number, y: number}} p
 * @param {{x: number, y: number}} q
 * @returns {'h'|'v'|null}
 */
function segmentOrientation(p, q) {
    if (Math.abs(p.y - q.y) <= 0.5 && Math.abs(p.x - q.x) > 0.5) return 'h';
    if (Math.abs(p.x - q.x) <= 0.5 && Math.abs(p.y - q.y) > 0.5) return 'v';
    return null;
}

/**
 * @param {Array<{x: number, y: number}>} points
 * @returns {Array<{index: number, length: number, orientation: 'h'|'v'|null, start: object, end: object}>}
 */
function polylineSegments(points) {
    const segs = [];
    if (!points || points.length < 2) return segs;
    for (let i = 1; i < points.length; i += 1) {
        const start = points[i - 1];
        const end = points[i];
        const length = Math.hypot(end.x - start.x, end.y - start.y);
        segs.push({
            index: i - 1,
            length,
            orientation: segmentOrientation(start, end),
            start,
            end,
        });
    }
    return segs;
}

/**
 * @param {Array<{x: number, y: number}>} points
 * @param {number} t
 * @returns {{index: number, orientation: 'h'|'v'|null, span: number}}
 */
function segmentAtT(points, t) {
    const segs = polylineSegments(points);
    const total = segs.reduce((sum, seg) => sum + seg.length, 0);
    if (total <= 0 || segs.length === 0) {
        return {index: 0, orientation: null, span: 0};
    }
    let remain = total * Math.min(1, Math.max(0, t));
    for (const seg of segs) {
        if (remain <= seg.length || seg.index === segs.length - 1) {
            return {index: seg.index, orientation: seg.orientation, span: seg.length};
        }
        remain -= seg.length;
    }
    const last = segs[segs.length - 1];
    return {index: last.index, orientation: last.orientation, span: last.length};
}

/**
 * Distance from an open segment to an AABB. 0 when they intersect.
 *
 * @param {{x: number, y: number}} p
 * @param {{x: number, y: number}} q
 * @param {{x: number, y: number, w: number, h: number}} rect
 * @returns {number}
 */
function segmentRectDistance(p, q, rect) {
    if (segmentIntersectsRect(p, q, rect, 0)) return 0;
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    const lenSq = dx * dx + dy * dy;
    const pointToRect = (pt) => {
        const hx = Math.max(rect.x - pt.x, 0, pt.x - (rect.x + rect.w));
        const hy = Math.max(rect.y - pt.y, 0, pt.y - (rect.y + rect.h));
        return Math.hypot(hx, hy);
    };
    const pointToSeg = (pt) => {
        if (lenSq === 0) return Math.hypot(pt.x - p.x, pt.y - p.y);
        let t = ((pt.x - p.x) * dx + (pt.y - p.y) * dy) / lenSq;
        t = Math.max(0, Math.min(1, t));
        return Math.hypot(pt.x - (p.x + t * dx), pt.y - (p.y + t * dy));
    };
    let min = Math.min(pointToRect(p), pointToRect(q));
    const corners = [
        {x: rect.x, y: rect.y},
        {x: rect.x + rect.w, y: rect.y},
        {x: rect.x, y: rect.y + rect.h},
        {x: rect.x + rect.w, y: rect.y + rect.h},
    ];
    for (const corner of corners) {
        min = Math.min(min, pointToSeg(corner));
    }
    return min;
}

/**
 * Count foreign strokes that cut (or graze) a label box.
 *
 * @param {{x: number, y: number, w: number, h: number}} box
 * @param {Array<{points?: Array<{x: number, y: number}>}|Array<{x: number, y: number}>>} otherEdges
 * @param {{pad?: number, nearPad?: number}} [opts]
 * @returns {{hits: number, nearHits: number, minDist: number}}
 */
export function countForeignEdgeLabelConflicts(box, otherEdges, opts = {}) {
    const nearPad = Number.isFinite(opts.nearPad)
        ? opts.nearPad
        : (Number.isFinite(opts.pad) ? opts.pad : LABEL_FOREIGN_EDGE_PAD);
    let hits = 0;
    let nearHits = 0;
    let minDist = Infinity;
    for (const edge of otherEdges || []) {
        const pts = edge?.points || edge;
        if (!pts || pts.length < 2) continue;
        let edgeMin = Infinity;
        let intersects = false;
        for (let i = 1; i < pts.length; i += 1) {
            if (segmentIntersectsRect(pts[i - 1], pts[i], box, 0)) {
                intersects = true;
                edgeMin = 0;
                break;
            }
            edgeMin = Math.min(edgeMin, segmentRectDistance(pts[i - 1], pts[i], box));
        }
        if (!Number.isFinite(edgeMin)) continue;
        minDist = Math.min(minDist, edgeMin);
        if (intersects || edgeMin < FOREIGN_FATAL_NEAR_PX) {
            hits += 1;
        } else if (edgeMin < nearPad) {
            nearHits += 1;
        }
    }
    return {hits, nearHits, minDist};
}

/**
 * Segment-aware candidate slots. A segment is kept only when its usable
 * interior can hold the label plus {@link LABEL_EDGE_PADDING} on each end.
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {{w: number, h: number}} labelSize
 * @returns {Array<{t: number, point: {x: number, y: number}, segmentIndex: number, orientation: 'h'|'v'|null, span: number, strict: boolean}>}
 */
export function labelSlotsForPolyline(points, labelSize) {
    const segs = polylineSegments(points);
    const total = segs.reduce((sum, seg) => sum + seg.length, 0);
    if (total <= 0) return [];

    const size = labelSize || {w: 0, h: 0};
    const slots = [];
    let prefix = 0;

    for (const seg of segs) {
        // Arrange labels sit on the stroke. A vertical host draws through the
        // text — never offer one when we can grow a horizontal instead.
        if (seg.orientation !== 'h') {
            prefix += seg.length;
            continue;
        }
        const dim = size.w || 0;
        const endInset = Math.max(LABEL_EDGE_PADDING, HOST_SEGMENT_INSET);
        const required = dim + endInset * 2;
        if (seg.length + 1e-6 >= required) {
            const half = dim / 2;
            const startOff = endInset + half;
            const endOff = seg.length - endInset - half;
            const usable = Math.max(0, endOff - startOff);
            const offsets = new Set();
            if (usable <= 1e-3) {
                offsets.add((startOff + endOff) / 2);
            } else {
                const step = Math.max(8, usable / 8);
                for (let off = startOff; off <= endOff + 1e-6; off += step) {
                    offsets.add(off);
                }
                offsets.add((startOff + endOff) / 2);
            }
            for (const offset of offsets) {
                const t = (prefix + offset) / total;
                const point = pointAlongPolyline(points, t);
                slots.push({
                    t,
                    point,
                    segmentIndex: seg.index,
                    orientation: seg.orientation,
                    span: seg.length,
                    strict: true,
                });
            }
        }
        prefix += seg.length;
    }

    slots.sort((a, b) => {
        if (a.orientation === 'h' && b.orientation !== 'h') return -1;
        if (b.orientation === 'h' && a.orientation !== 'h') return 1;
        if (b.span !== a.span) return b.span - a.span;
        return Math.abs(a.t - 0.5) - Math.abs(b.t - 0.5);
    });
    return slots;
}

/**
 * Uniform-t fallback so a short/diagonal path still has somewhere to slide.
 *
 * @param {Array<{x: number, y: number}>} points
 * @returns {Array<object>}
 */
function fallbackSlotsForPolyline(points) {
    const slots = [];
    if (!points || points.length < 2) return slots;
    for (let i = 15; i <= 85; i += 3) {
        const t = i / 100;
        const at = segmentAtT(points, t);
        if (at.orientation !== 'h') continue;
        slots.push({
            t,
            point: pointAlongPolyline(points, t),
            segmentIndex: at.index,
            orientation: at.orientation,
            span: at.span,
            strict: false,
        });
    }
    return slots;
}

/**
 * Extra inset samples on the longest *horizontal* run. Vertical-only
 * polylines return nothing — Arrange must grow a horizontal first.
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {{w: number, h: number}} alongSize
 * @returns {Array<object>}
 */
function insetSlotsOnLongestSegment(points, alongSize) {
    const segs = polylineSegments(points);
    const total = segs.reduce((sum, seg) => sum + seg.length, 0);
    if (total <= 0) return [];
    const horizontals = segs.filter((seg) => seg.orientation === 'h');
    if (!horizontals.length) return [];
    let best = horizontals[0];
    for (const seg of horizontals) {
        if (seg.length > best.length) best = seg;
    }
    const dim = best.orientation === 'v' ? (alongSize.h || 0) : (alongSize.w || 0);
    const half = dim / 2;
    const inset = Math.max(HOST_SEGMENT_INSET, LABEL_ELBOW_VERTEX_PAD) + half;
    let prefix = 0;
    for (const seg of segs) {
        if (seg.index === best.index) break;
        prefix += seg.length;
    }
    const lo = Math.min(inset, best.length / 2);
    const hi = Math.max(best.length - inset, best.length / 2);
    const offsets = new Set([lo, (lo + hi) / 2, hi]);
    return [...offsets].map((offset) => {
        const t = (prefix + offset) / total;
        return {
            t,
            point: pointAlongPolyline(points, t),
            segmentIndex: best.index,
            orientation: best.orientation,
            span: best.length,
            strict: false,
        };
    });
}

/**
 * True when the label box stays inside the hosting segment, short of the
 * vertices — so a horizontal label cannot reach the vertical drop.
 *
 * @param {{segmentIndex?: number, orientation?: string|null}} slot
 * @param {{x: number, y: number, w: number, h: number}} box
 * @param {Array<{x: number, y: number}>} ownPoints
 * @param {number} [pad]
 * @returns {boolean}
 */
function labelBoxFitsHostSegment(slot, box, ownPoints, pad = HOST_SEGMENT_INSET) {
    if (!ownPoints || ownPoints.length < 2 || slot.segmentIndex == null) {
        return true;
    }
    const segs = polylineSegments(ownPoints);
    const seg = segs[slot.segmentIndex];
    if (!seg || !seg.orientation) {
        return true;
    }
    if (seg.orientation === 'h') {
        const lo = Math.min(seg.start.x, seg.end.x) + pad;
        const hi = Math.max(seg.start.x, seg.end.x) - pad;
        return box.x >= lo - 1e-6 && box.x + box.w <= hi + 1e-6;
    }
    const lo = Math.min(seg.start.y, seg.end.y) + pad;
    const hi = Math.max(seg.start.y, seg.end.y) - pad;
    return box.y >= lo - 1e-6 && box.y + box.h <= hi + 1e-6;
}

/**
 * @param {Array<string>|Set<string>|undefined} ids
 * @returns {Set<string>}
 */
function asIdSet(ids) {
    if (ids instanceof Set) return ids;
    return new Set(ids || []);
}

function pointInInflatedBox(point, box, pad) {
    return point.x >= box.x - pad
        && point.x <= box.x + box.w + pad
        && point.y >= box.y - pad
        && point.y <= box.y + box.h + pad;
}

/**
 * Rect-based slot score. Hard conflicts (card / other label / edge through
 * the box) set `conflicts` so the Arrange repair loop can retry.
 *
 * Own-edge crossings and endpoint-card overlap are fatal: a slot whose box
 * hits this edge's other segments or either endpoint card is never "clear".
 *
 * @param {{t: number, point: {x: number, y: number}, segmentIndex?: number, orientation?: string|null, span?: number, strict?: boolean}} slot
 * @param {{
 *   cards?: Array<{x: number, y: number, w: number, h: number, id?: string}>,
 *   endpointCardIds?: Array<string>|Set<string>,
 *   placedLabelBoxes?: Array<{x: number, y: number, w: number, h: number}>,
 *   otherEdges?: Array<{points?: Array<{x: number, y: number}>}>,
 *   ownPoints?: Array<{x: number, y: number}>,
 *   labelSize?: {w: number, h: number},
 *   label?: string|null,
 *   labelPoints?: Array<{x: number, y: number}>,
 * }} [context]
 * @returns {{score: number, conflicts: boolean, foreignHits: number, nearHits: number}}
 */
export function scoreLabelSlot(slot, context = {}) {
    const cards = context.cards || [];
    const endpointCardIds = asIdSet(context.endpointCardIds);
    const placedLabelBoxes = context.placedLabelBoxes || [];
    const otherEdges = context.otherEdges || [];
    const ownPoints = context.ownPoints || [];
    const labelPoints = context.labelPoints || [];
    const labelSize = context.labelSize || {w: 0, h: 0};
    const size = (labelSize.w > 0 || labelSize.h > 0)
        ? labelSize
        : {w: 24, h: 16};
    const fitBox = labelBoxAt(slot.point, size);
    const box = collisionLabelBoxAt(slot.point, size);
    const lineCount = Number.isFinite(context.lineCount)
        ? context.lineCount
        : Math.max(1, Math.round(((size.h || 0) - 10) / 16) || 1);

    let score = 0;
    let conflicts = false;

    if (slot.orientation === 'v') {
        score -= VERTICAL_HOST_PENALTY;
        conflicts = true;
    }

    for (const card of cards) {
        const isEndpoint = (card.id && endpointCardIds.has(card.id)) || card.endpoint;
        const pad = isEndpoint ? LABEL_ENDPOINT_CARD_PAD : LABEL_CARD_PAD;
        if (rectsOverlap(box, card, pad)) {
            score -= CARD_OVERLAP_PENALTY;
            conflicts = true;
        }
    }

    for (const other of placedLabelBoxes) {
        if (rectsOverlap(box, other, LABEL_BOX_PAD)) {
            score -= LABEL_OVERLAP_PENALTY;
            conflicts = true;
        }
    }

    if (ownPoints.length >= 2) {
        if (!labelBoxFitsHostSegment(slot, fitBox, ownPoints)) {
            score -= OWN_SEGMENT_CROSS_PENALTY;
            conflicts = true;
        }
        for (let i = 1; i < ownPoints.length; i += 1) {
            if (i - 1 === slot.segmentIndex) continue;
            if (segmentIntersectsRect(ownPoints[i - 1], ownPoints[i], fitBox, 0)) {
                score -= OWN_SEGMENT_CROSS_PENALTY;
                conflicts = true;
                break;
            }
        }
        if (lineCount >= 2) {
            for (const vertex of ownPoints) {
                if (pointInInflatedBox(vertex, fitBox, LABEL_ELBOW_VERTEX_PAD)) {
                    score -= VERTEX_PENALTY;
                    conflicts = true;
                    break;
                }
            }
        }
    }

    const foreign = countForeignEdgeLabelConflicts(box, otherEdges, {
        pad: LABEL_FOREIGN_EDGE_PAD,
        nearPad: LABEL_FOREIGN_EDGE_PAD,
    });
    if (foreign.hits > 0) {
        score -= EDGE_THROUGH_LABEL_PENALTY * foreign.hits;
        conflicts = true;
    }
    if (foreign.nearHits > 0) {
        const closeness = Number.isFinite(foreign.minDist)
            ? Math.max(0, LABEL_FOREIGN_EDGE_PAD - foreign.minDist)
            : LABEL_FOREIGN_EDGE_PAD;
        score -= NEAR_MISS_PENALTY * foreign.nearHits + closeness * NEAR_MISS_PER_PX;
    }

    let minD = Infinity;
    for (const other of labelPoints) {
        minD = Math.min(minD, Math.hypot(slot.point.x - other.x, slot.point.y - other.y));
    }
    if (Number.isFinite(minD)) {
        score += minD;
        if (minD < LABEL_POINT_SEP) {
            score -= (LABEL_POINT_SEP - minD) * 6;
        }
    }

    const along = slot.orientation === 'v' ? size.h : size.w;
    const required = along + LABEL_EDGE_PADDING * 2;
    if (Number.isFinite(slot.span)) {
        if (slot.span + 1e-6 < required) {
            score -= SHORT_SPAN_PENALTY;
            conflicts = true;
        }
        score += Math.min(slot.span, 800) * SPAN_SCORE_PER_PX;
    }

    // Horizontal bonus only for a strict, long-enough run — a short inbound
    // stub must not beat a longer vertical just because it is horizontal.
    if (slot.orientation === 'h' && slot.strict) score += HORIZONTAL_BONUS;
    if (slot.strict) score += STRICT_SLOT_BONUS;
    score -= Math.abs((slot.t ?? 0.5) - 0.5) * 12;

    return {
        score,
        conflicts,
        foreignHits: foreign.hits,
        nearHits: foreign.nearHits,
    };
}

/**
 * Route-score term: no clean 1-D slot and foreign hits are expensive;
 * a longer horizontal host is preferred.
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {{
 *   label?: string|null,
 *   cards?: Array<object>,
 *   obstacles?: Array<object>,
 *   otherEdges?: Array<object>,
 *   endpointCardIds?: Array<string>|Set<string>,
 *   endpointCards?: Array<object>,
 * }} [opts]
 * @returns {number}
 */
export function labelPlacementRoutePenalty(points, opts = {}) {
    if (!opts.label || !points || points.length < 2) return 0;
    const alongSize = placementLabelSize(opts.label, opts.labelSize);
    const strict = labelSlotsForPolyline(points, alongSize);
    if (strict.length === 0) return NO_CLEAR_SLOT_ROUTE_PENALTY;

    const slot = strict[0];
    const box = collisionLabelBoxAt(slot.point, alongSize);
    const foreign = countForeignEdgeLabelConflicts(box, opts.otherEdges || [], {
        pad: LABEL_FOREIGN_EDGE_PAD,
        nearPad: LABEL_FOREIGN_EDGE_PAD,
    });
    if (foreign.hits > 0) {
        return NO_CLEAR_SLOT_ROUTE_PENALTY + FOREIGN_HIT_ROUTE_PENALTY;
    }
    let penalty = 0;
    if (slot.orientation === 'h') penalty -= HORIZONTAL_SLOT_ROUTE_BONUS;
    penalty -= Math.min(slot.span || 0, 800) * 0.2;
    if (foreign.nearHits > 0) penalty += NEAR_MISS_PENALTY;
    return penalty;
}

function rankSlots(candidates, context) {
    let best = null;
    let bestClear = null;
    let bestNoForeign = null;
    for (const slot of candidates) {
        const scored = scoreLabelSlot(slot, context);
        const ranked = {slot, ...scored};
        if (!best || ranked.score > best.score) best = ranked;
        if (!ranked.conflicts && (!bestClear || ranked.score > bestClear.score)) {
            bestClear = ranked;
        }
        if (ranked.foreignHits === 0 && (!bestNoForeign || ranked.score > bestNoForeign.score)) {
            bestNoForeign = ranked;
        }
    }
    return {best, bestClear, bestNoForeign};
}

/**
 * Best *conflict-free* slot `t` on this polyline. Horizontal hosts only.
 * Returns 0.5 when no clear seat exists so callers can lengthen / offset
 * corridors instead of committing a stacked or vertical placement.
 *
 * @param {Array<{x: number, y: number}>} points
 * @param {{
 *   label?: string|null,
 *   labelSize?: {w: number, h: number},
 *   cards?: Array<object>,
 *   obstacles?: Array<object>,
 *   placedLabelBoxes?: Array<object>,
 *   labelBoxes?: Array<object>,
 *   otherEdges?: Array<object>,
 *   labelPoints?: Array<{x: number, y: number}>,
 *   endpointCardIds?: Array<string>|Set<string>,
 *   endpointCards?: Array<object>,
 * }} [opts]
 * @returns {number}
 */
export function pickLabelPositionForEdge(points, opts = {}) {
    if (!points || points.length < 2) return 0.5;
    const labelSize = placementLabelSize(opts.label, opts.labelSize);
    const cards = [
        ...(opts.cards || opts.obstacles || []),
        ...(opts.endpointCards || []),
    ];
    const placedLabelBoxes = opts.placedLabelBoxes || opts.labelBoxes || [];
    const endpointCardIds = asIdSet(opts.endpointCardIds);
    for (const card of opts.endpointCards || []) {
        if (card?.id) endpointCardIds.add(card.id);
    }
    const context = {
        cards,
        endpointCardIds,
        placedLabelBoxes,
        otherEdges: opts.otherEdges || [],
        ownPoints: points,
        labelSize,
        label: opts.label,
        lineCount: estimateLabelLineCount(opts.label),
        labelPoints: opts.labelPoints || [],
    };

    const strict = labelSlotsForPolyline(points, labelSize);
    const fallback = fallbackSlotsForPolyline(points);
    const lastResort = insetSlotsOnLongestSegment(points, labelSize);
    const candidates = [
        ...(strict.length ? strict : []),
        ...fallback,
        ...lastResort,
    ].filter((slot) => slot.orientation === 'h');
    if (candidates.length === 0) return 0.5;

    const ranked = rankSlots(candidates, context);
    // Prefer a clear seat; fall back to the highest-scoring (least-bad) seat
    // rather than always returning 0.5, so crowded-line labels slide as far
    // away from each other as the geometry allows.
    return ranked.bestClear
        ? ranked.bestClear.slot.t
        : ranked.best
            ? ranked.best.slot.t
            : 0.5;
}

/**
 * @param {object|null|undefined} shape
 * @returns {string}
 */
function labelTextFromArrow(shape) {
    const content = shape?.props?.richText?.content;
    if (!Array.isArray(content)) return '';
    for (const para of content) {
        for (const node of para?.content || []) {
            if (node?.type === 'text' && node.text) {
                return String(node.text).trim();
            }
        }
    }
    return '';
}

/**
 * @param {import('tldraw').Editor} editor
 * @param {string} shapeId
 * @returns {{x: number, y: number, w: number, h: number}|null}
 */
function shapeRect(editor, shapeId) {
    const shape = editor.getShape?.(shapeId);
    const pageBounds = editor.getShapePageBounds?.(shapeId);
    if (pageBounds) {
        return {
            x: pageBounds.x,
            y: pageBounds.y,
            w: pageBounds.w || shape?.props?.w || ENTITY_CARD_WIDTH,
            h: Math.max(pageBounds.h || 0, shape?.props?.h || 0),
        };
    }
    if (!shape) return null;
    return {
        x: shape.x,
        y: shape.y,
        w: shape.props?.w || ENTITY_CARD_WIDTH,
        h: Math.max(shape.props?.h || 0, 80),
    };
}

/**
 * @param {import('tldraw').Editor} editor
 * @returns {Array<{x: number, y: number, w: number, h: number, id: string}>}
 */
function collectCardRects(editor) {
    const cards = [];
    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== ENTITY_CARD_SHAPE_TYPE) continue;
        const rect = shapeRect(editor, shape.id);
        if (rect) cards.push({...rect, id: shape.id});
    }
    return cards;
}

/**
 * @param {import('tldraw').Editor} editor
 * @param {object} shape
 * @returns {{
 *   points: Array<{x: number, y: number}>,
 *   startId: string,
 *   endId: string,
 *   label: string,
 * }|null}
 */
function arrowBindings(editor, shape) {
    // Lazy so chartArrowRouting can import this module without loading tldraw
    // (its unit tests do not mock the package). Requiring our own wrapper
    // (instead of 'tldraw' directly) avoids webpack loading a second,
    // CommonJS copy of tldraw alongside the ESM copy used everywhere else —
    // see tldrawArrowBindings.js for why that causes the "multiple
    // instances of some tldraw libraries" crash during chart layout.
    const {getArrowBindings} = require('./tldrawArrowBindings');
    return getArrowBindings(editor, shape) || {};
}

function arrowPolylineFromEditor(editor, shape) {
    const bindings = arrowBindings(editor, shape);
    const startId = bindings?.start?.toId;
    const endId = bindings?.end?.toId;
    if (!startId || !endId) return null;
    const fromRect = shapeRect(editor, startId);
    const toRect = shapeRect(editor, endId);
    if (!fromRect || !toRect) return null;
    const fromSide = sideFromNormalizedAnchor(bindings.start?.props?.normalizedAnchor)
        || facingSide(fromRect, toRect);
    const toSide = sideFromNormalizedAnchor(bindings.end?.props?.normalizedAnchor)
        || facingSide(toRect, fromRect);
    const fromT = anchorTFromNormalized(bindings.start?.props?.normalizedAnchor);
    const toT = anchorTFromNormalized(bindings.end?.props?.normalizedAnchor);
    const points = shape.props?.kind === 'arc'
        ? buildArcPolyline(fromRect, toRect, {
            fromSide,
            toSide,
            fromT,
            toT,
            bend: shape.props?.bend ?? 40,
        })
        : buildElbowPolyline(fromRect, toRect, {
            fromSide,
            toSide,
            fromT,
            toT,
            midPoint: shape.props?.elbowMidPoint ?? 0.5,
        });
    return {
        points,
        startId,
        endId,
        label: labelTextFromArrow(shape),
    };
}

/**
 * @param {import('tldraw').Editor} editor
 * @returns {Array<{
 *   shape: object,
 *   points: Array<{x: number, y: number}>,
 *   startId: string,
 *   endId: string,
 *   label: string,
 *   labelSize: {w: number, h: number},
 * }>}
 */
function collectLabeledArrows(editor) {
    const items = [];
    for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (shape?.type !== 'arrow') continue;
        const poly = arrowPolylineFromEditor(editor, shape);
        if (!poly?.label) continue;
        items.push({
            shape,
            points: poly.points,
            startId: poly.startId,
            endId: poly.endId,
            label: poly.label,
            labelSize: placementLabelSize(poly.label),
            lineCount: estimateLabelLineCount(poly.label),
        });
    }
    return items;
}

function pickAssignedSlot(item, placedLabelBoxes, otherEdges, cards, skipTs = []) {
    const strict = labelSlotsForPolyline(item.points, item.labelSize);
    const fallback = fallbackSlotsForPolyline(item.points);
    const lastResort = insetSlotsOnLongestSegment(item.points, item.labelSize);
    const candidates = [...strict, ...fallback, ...lastResort]
        .filter((slot) => slot.orientation === 'h')
        .filter((slot) => !skipTs.some((t) => Math.abs(t - slot.t) < 1e-4));
    const context = {
        cards,
        endpointCardIds: new Set([item.startId, item.endId]),
        placedLabelBoxes,
        otherEdges,
        ownPoints: item.points,
        labelSize: item.labelSize,
        label: item.label,
        lineCount: item.lineCount,
    };
    const ranked = rankSlots(candidates, context);
    return ranked.bestClear || null;
}

/**
 * Global assignment: largest labels pick first, then a bounded repair loop
 * retries the next-best slot for anything still in conflict.
 *
 * @param {import('tldraw').Editor} editor
 * @returns {{conflicts: number}}
 */
export function resolveChartLabelPlacements(editor) {
    if (!editor) return {conflicts: 0};
    const cards = collectCardRects(editor);
    const items = collectLabeledArrows(editor);
    if (items.length === 0) return {conflicts: 0};

    items.sort((a, b) => (b.labelSize.w * b.labelSize.h) - (a.labelSize.w * a.labelSize.h));

    const otherEdgesFor = (current) => items
        .filter((item) => item.shape.id !== current.shape.id)
        .map((item) => ({points: item.points}));

    /** @type {Array<{item: object, slot: object, conflicts: boolean, tried: number[]}>} */
    const assigned = [];
    let placed = [];

    for (const item of items) {
        const pick = pickAssignedSlot(item, placed, otherEdgesFor(item), cards);
        if (!pick) continue;
        assigned.push({
            item,
            slot: pick.slot,
            conflicts: pick.conflicts,
            tried: [pick.slot.t],
        });
        placed.push(collisionLabelBoxAt(pick.slot.point, item.labelSize));
    }

    for (let round = 0; round < MAX_REPAIR_ROUNDS; round += 1) {
        let repaired = false;
        placed = assigned.map(({item, slot}) => collisionLabelBoxAt(slot.point, item.labelSize));
        for (let i = 0; i < assigned.length; i += 1) {
            const entry = assigned[i];
            const others = placed.filter((_, idx) => idx !== i);
            const context = {
                cards,
                endpointCardIds: new Set([entry.item.startId, entry.item.endId]),
                placedLabelBoxes: others,
                otherEdges: otherEdgesFor(entry.item),
                ownPoints: entry.item.points,
                labelSize: entry.item.labelSize,
                label: entry.item.label,
                lineCount: entry.item.lineCount,
            };
            const current = scoreLabelSlot(entry.slot, context);
            entry.conflicts = current.conflicts;
            if (!entry.conflicts) continue;
            const pick = pickAssignedSlot(entry.item, others, otherEdgesFor(entry.item), cards, entry.tried);
            if (!pick || pick.slot.t === entry.slot.t) continue;
            entry.slot = pick.slot;
            entry.conflicts = pick.conflicts;
            entry.tried.push(pick.slot.t);
            placed[i] = collisionLabelBoxAt(pick.slot.point, entry.item.labelSize);
            repaired = true;
        }
        if (!repaired) break;
    }

    const updates = assigned
        .filter((entry) => entry.slot && !entry.conflicts)
        .map(({item, slot}) => ({
            id: item.shape.id,
            type: 'arrow',
            props: {
                ...item.shape.props,
                labelPosition: slot.t,
            },
        }));
    if (updates.length) {
        editor.updateShapes?.(updates);
    }
    return {conflicts: assigned.filter((entry) => entry.conflicts).length};
}

/**
 * Extra length needed so a blocked host can grow a clear 1-D window.
 *
 * @param {object} bestSeg
 * @param {{w: number, h: number}} alongSize
 * @param {object} context
 * @returns {{deficit: number, reason: 'foreign-edge'|'blocked'|null}}
 */
/**
 * When two parallel hosts are closer than both collision boxes, slide is
 * impossible — every seat is a foreign stroke through the text. Offset the
 * whole corridor on the perpendicular axis.
 *
 * @param {object} item
 * @param {Array<object>} items
 * @returns {{axis: 'x'|'y', deficit: number, sign: number, reason: string}|null}
 */
function parallelHostCorridorDeficit(item, items) {
    const ownSegs = polylineSegments(item.points).filter((seg) => seg.orientation === 'h' || seg.orientation === 'v');
    if (!ownSegs.length) return null;
    const ownH = (item.labelSize?.h || 0) + LABEL_REVIEW_ICON_PAD;
    const ownW = (item.labelSize?.w || 0) + LABEL_REVIEW_ICON_PAD;
    let worst = null;
    for (const other of items) {
        if (other.shape.id === item.shape.id) continue;
        const otherSegs = polylineSegments(other.points)
            .filter((seg) => seg.orientation === 'h' || seg.orientation === 'v');
        const otherH = (other.labelSize?.h || 0) + LABEL_REVIEW_ICON_PAD;
        const otherW = (other.labelSize?.w || 0) + LABEL_REVIEW_ICON_PAD;
        for (const own of ownSegs) {
            for (const foreign of otherSegs) {
                if (own.orientation !== foreign.orientation) continue;
                const horizontal = own.orientation === 'h';
                const ownLo = horizontal
                    ? Math.min(own.start.x, own.end.x)
                    : Math.min(own.start.y, own.end.y);
                const ownHi = horizontal
                    ? Math.max(own.start.x, own.end.x)
                    : Math.max(own.start.y, own.end.y);
                const foreignLo = horizontal
                    ? Math.min(foreign.start.x, foreign.end.x)
                    : Math.min(foreign.start.y, foreign.end.y);
                const foreignHi = horizontal
                    ? Math.max(foreign.start.x, foreign.end.x)
                    : Math.max(foreign.start.y, foreign.end.y);
                if (ownHi < foreignLo + 1 || foreignHi < ownLo + 1) continue;
                const ownCoord = horizontal ? own.start.y : own.start.x;
                const foreignCoord = horizontal ? foreign.start.y : foreign.start.x;
                const gap = Math.abs(ownCoord - foreignCoord);
                const needed = horizontal
                    ? (ownH + otherH) / 2 + LABEL_BOX_PAD
                    : (ownW + otherW) / 2 + LABEL_BOX_PAD;
                if (gap + 1e-6 >= needed) continue;
                const deficit = needed - gap;
                if (!worst || deficit > worst.deficit) {
                    worst = {
                        axis: horizontal ? 'y' : 'x',
                        deficit,
                        sign: ownCoord <= foreignCoord ? -1 : 1,
                        reason: 'corridor-offset',
                    };
                }
            }
        }
    }
    return worst;
}

function blockedHostDeficit(bestSeg, alongSize, context) {
    const along = bestSeg.orientation === 'v' ? alongSize.h : alongSize.w;
    const required = along + LABEL_EDGE_PADDING * 2;
    const boxW = bestSeg.orientation === 'v' ? alongSize.w : along;
    const boxH = bestSeg.orientation === 'v' ? along : alongSize.h;
    const mid = {
        x: (bestSeg.start.x + bestSeg.end.x) / 2,
        y: (bestSeg.start.y + bestSeg.end.y) / 2,
    };
    const swept = collisionLabelBoxAt(mid, {w: boxW, h: boxH});
    if (bestSeg.orientation === 'h') {
        swept.x = Math.min(bestSeg.start.x, bestSeg.end.x);
        swept.w = bestSeg.length;
    } else if (bestSeg.orientation === 'v') {
        swept.y = Math.min(bestSeg.start.y, bestSeg.end.y);
        swept.h = bestSeg.length;
    }
    const foreign = countForeignEdgeLabelConflicts(swept, context.otherEdges, {
        pad: LABEL_FOREIGN_EDGE_PAD,
        nearPad: LABEL_FOREIGN_EDGE_PAD,
    });
    const reason = foreign.hits > 0 || foreign.nearHits > 0 ? 'foreign-edge' : 'blocked';
    const extra = along + LABEL_FOREIGN_EDGE_PAD * 2;
    return {
        deficit: Math.max(required - bestSeg.length, extra, MIN_LENGTHEN_WHEN_BLOCKED),
        reason,
    };
}

/**
 * Edges whose current route has no conflict-free slot long enough for the label.
 * Reports a deficit when the host is geometrically short *or* every 1-D slot
 * is blocked (foreign edge / near-miss / card) so Arrange can push cards apart.
 *
 * @param {import('tldraw').Editor} editor
 * @returns {Array<{startId: string, endId: string, axis: 'x'|'y', deficit: number, reason?: string}>}
 */
export function labelLengthDeficits(editor) {
    if (!editor) return [];
    const cards = collectCardRects(editor);
    const items = collectLabeledArrows(editor);
    const deficits = [];

    for (const item of items) {
        const segs = polylineSegments(item.points);
        if (segs.length === 0) continue;
        const otherEdges = items
            .filter((other) => other.shape.id !== item.shape.id)
            .map((other) => ({points: other.points}));
        const placedLabelBoxes = items
            .filter((other) => other.shape.id !== item.shape.id)
            .map((other) => collisionLabelBoxAt(
                pointAlongPolyline(other.points, other.shape.props?.labelPosition ?? 0.5),
                other.labelSize,
            ));
        const context = {
            cards,
            endpointCardIds: new Set([item.startId, item.endId]),
            placedLabelBoxes,
            otherEdges,
            ownPoints: item.points,
            labelSize: item.labelSize,
            label: item.label,
            lineCount: item.lineCount,
        };

        const horizontals = segs.filter((seg) => seg.orientation === 'h');
        if (horizontals.length === 0) {
            const required = (item.labelSize.w || 0)
                + LABEL_EDGE_PADDING * 2
                + HOST_SEGMENT_INSET * 2;
            deficits.push({
                startId: item.startId,
                endId: item.endId,
                axis: 'x',
                deficit: Math.max(required, MIN_LENGTHEN_WHEN_BLOCKED),
                reason: 'need-horizontal',
            });
            continue;
        }

        const slots = labelSlotsForPolyline(item.points, item.labelSize);
        const fallback = slots.length ? slots : fallbackSlotsForPolyline(item.points);
        const hasClear = fallback.some((slot) => !scoreLabelSlot(slot, context).conflicts);

        let bestSeg = horizontals[0];
        for (const seg of horizontals) {
            if (seg.length > bestSeg.length) bestSeg = seg;
        }
        const along = bestSeg.orientation === 'v' ? item.labelSize.h : item.labelSize.w;
        const required = along + LABEL_EDGE_PADDING * 2;
        const geometric = required - bestSeg.length;

        if (hasClear && slots.length > 0 && geometric <= 0) continue;

        if (!hasClear) {
            const corridor = parallelHostCorridorDeficit(item, items);
            if (corridor) {
                deficits.push({
                    startId: item.startId,
                    endId: item.endId,
                    axis: corridor.axis,
                    deficit: corridor.deficit,
                    sign: corridor.sign,
                    reason: corridor.reason,
                });
                continue;
            }
        }

        let deficit = geometric;
        let reason = 'short-segment';
        if (!hasClear) {
            const blocked = blockedHostDeficit(bestSeg, item.labelSize, context);
            deficit = Math.max(geometric, blocked.deficit);
            reason = blocked.reason;
        }
        if (deficit <= 0) continue;
        deficits.push({
            startId: item.startId,
            endId: item.endId,
            axis: bestSeg.orientation === 'v' ? 'y' : 'x',
            deficit,
            reason,
        });
    }

    return deficits;
}
